import { apiClient } from '@/common/lib';

/**
 * Authentication API declarations — define and export request functions here.
 */

// Login user with credentials (email/mobile + password)
export async function loginUser(credentials) {
  const formData = new FormData();
  formData.append(
    'email',
    credentials.emailOrMobile || credentials.email || '',
  );
  formData.append('password', credentials.password || '');

  const { data } = await apiClient.post('/v1/accounts/login/email/', formData);
  return data;
}

// Register user using Email
export async function registerWithEmail(registerData) {
  const formData = new FormData();
  formData.append('email', registerData.email || '');
  formData.append('password', registerData.password || '');

  const { data } = await apiClient.post(
    '/v1/accounts/register/email/',
    formData,
  );
  return data;
}

// Register user using Mobile Number (Send OTP)
export async function registerWithMobile(registerData) {
  const formData = new FormData();
  formData.append('phone', registerData.mobile || registerData.phone || '');
  formData.append('purpose', 'registration');

  const { data } = await apiClient.post(
    '/v1/accounts/register/phone/send-otp/',
    formData,
  );
  return data;
}

// Send OTP for Login
export async function loginSendOtp(loginData) {
  const formData = new FormData();
  formData.append('phone', loginData.phone || '');
  formData.append('purpose', 'login');

  const { data } = await apiClient.post(
    '/v1/accounts/register/phone/send-otp/',
    formData,
  );
  return data;
}

// Request OTP sending (to email or mobile)
export async function sendOtp(otpRequestData) {
  const { target, flow } = otpRequestData;
  if (flow === 'register_mobile') {
    const formData = new FormData();
    formData.append('phone', target || '');
    const { data } = await apiClient.post(
      '/v1/accounts/otp/resend-otp/',
      formData,
    );
    return data;
  } else if (flow === 'login') {
    const formData = new FormData();
    formData.append('phone', target || '');
    formData.append('purpose', 'login');
    const { data } = await apiClient.post(
      '/v1/accounts/register/phone/send-otp/',
      formData,
    );
    return data;
  } else if (flow === 'verification') {
    const { data } = await apiClient.post('/v1/accounts/otp/resend-otp/', {
      phone: target || '',
    });
    return data;
  }
  const { data } = await apiClient.post('/auth/send-otp', otpRequestData);
  return data;
}

// Verify OTP
export async function verifyOtp(otpVerificationData) {
  const { target, code, flow } = otpVerificationData;
  if (flow === 'register_mobile') {
    const formData = new FormData();
    formData.append('phone', target || '');
    formData.append('otp_code', code || '');
    const { data } = await apiClient.post(
      '/v1/accounts/register/phone/',
      formData,
    );
    return data;
  } else if (flow === 'login') {
    const formData = new FormData();
    formData.append('phone', target || '');
    formData.append('otp_code', code || '');
    const { data } = await apiClient.post(
      '/v1/accounts/login/phone/',
      formData,
    );
    return data;
  } else if (flow === 'verification') {
    const formData = new FormData();
    formData.append('email', target || '');
    formData.append('otp_code', code || '');
    const { data } = await apiClient.post(
      '/v1/accounts/password/verify-otp/',
      formData,
    );
    return data;
  } else {
    const { data } = await apiClient.post(
      '/auth/verify-otp',
      otpVerificationData,
    );
    return data;
  }
}

// Reset Password
export async function resetPassword(resetData) {
  const formData = new FormData();
  formData.append('email', resetData.email || '');
  formData.append('otp_code', resetData.otp_code || '');
  formData.append('new_password', resetData.password || '');
  formData.append('confirm_password', resetData.confirmPassword || '');

  const { data } = await apiClient.post(
    '/v1/accounts/password/reset/',
    formData,
  );
  return data;
}

// Request password reset instructions
export async function forgotPassword(forgotPasswordData) {
  const { data } = await apiClient.post(
    '/v1/accounts/password/forgot/',
    forgotPasswordData,
  );
  return data;
}

// Register Company
export async function registerCompany(companyData) {
  const { data } = await apiClient.post('/v1/companies/', companyData);
  return data;
}

// Get available integrations
export async function getIntegrations() {
  const { data } = await apiClient.get('/v1/integrations/');
  return data;
}

// Setup WhatsApp Integration for a Company
export async function setupWhatsApp({ companyId, mobileNumber, businessName }) {
  const formData = new FormData();
  formData.append('mobile_number', mobileNumber);
  formData.append('business_name', businessName);

  const { data } = await apiClient.post(
    `/v1/integrations/companies/${companyId}/whatsapp/setup/`,
    formData,
  );
  return data;
}

// Meta Embedded Signup — get FB SDK / login config
export async function getWhatsAppEmbeddedSignupConfig(companyId) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/auth/embedded-signup/`,
  );
  return data;
}

// Meta Embedded Signup — exchange oauth code + session for connected credentials
export async function completeWhatsAppEmbeddedSignup(companyId, body) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/auth/embedded-signup/complete/`,
    body,
  );
  return data;
}

// Select an Integration (Step 2.2)
export async function selectIntegration({ companyId, integrationSlug }) {
  const { data } = await apiClient.post(
    `/v1/integrations/companies/${companyId}/select/`,
    { integration: integrationSlug },
  );
  return data;
}

// Get Setup Activation Status (Step 4 Polling)
export async function getIntegrationStatus(
  { companyId, integrationSlug },
  config = {},
) {
  const { data } = await apiClient.get(
    `/v1/integrations/companies/${companyId}/${integrationSlug}/status/`,
    config,
  );
  return data;
}

// Get Subscription Plans (Step 5)
export async function getSubscriptionPlans(integrationSlug, coupon = '') {
  const params = { integration: integrationSlug };
  if (coupon) {
    params.coupon = coupon;
  }
  const { data } = await apiClient.get(`/v1/subscriptions/plans/`, {
    params,
  });
  return data;
}

// Create Subscription Razorpay Order (Step 6)
export async function createSubscriptionOrder({ companyId, planSlug, coupon }) {
  const payload = { plan_slug: planSlug };
  if (coupon) {
    payload.coupon = coupon;
  }
  const { data } = await apiClient.post(
    `/v1/subscriptions/companies/${companyId}/orders/`,
    payload,
  );
  return data;
}

// Verify Subscription Razorpay Payment (Step 8)
export async function verifySubscriptionPayment({
  companyId,
  subscriptionId,
  paymentId,
  signature,
}) {
  const { data } = await apiClient.post(
    `/v1/subscriptions/companies/${companyId}/verify-payment/`,
    {
      razorpay_subscription_id: subscriptionId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    },
  );
  return data;
}

/**
 * List all invoices for a company.
 * GET /v1/subscriptions/companies/{company_id}/invoices/
 */
export async function getCompanyInvoices(companyId) {
  const { data } = await apiClient.get(
    `/v1/subscriptions/companies/${companyId}/invoices/`,
  );
  return data;
}

// Logout user
export async function logoutUser(refreshToken) {
  const formData = new FormData();
  formData.append('refresh', refreshToken || '');

  const { data } = await apiClient.post('/v1/accounts/logout/', formData);
  return data;
}
